import React, { useRef } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { AnimatePresence } from 'framer-motion'
import { useCommandBar } from 'contexts/command-bar'
import { useServiceOnboarding } from 'contexts/service-onboarding'
import { useStorage } from 'hooks/storage'
import { cls } from 'utilities/dom'
import { getCategoryIcon } from 'utilities/categories'
import { useGetItemLabel } from '../../utilities'
import {
    ListItem,
    Button, Item,
    Label, NewBadge, KeyHintInner
} from './s'
import {
    CornerDownLeft as Enter,
    ExternalLink,
    ArrowRight as Navigate
} from 'styled-icons/feather'
import { itemHeight } from './constants'

const Command = ({ item, isActive }) => {
    const { formatMessage } = useIntl()

    const itemRef = useRef()

    const [dismissedNewlyAdded] = useStorage({
        key: 'dismissed:newly-added',
        defaultValue: []
    })

    const { newBadgeItems } = useServiceOnboarding()
    const { execute } = useCommandBar()
    const getItemLabel = useGetItemLabel()

    const {
        actionId,
        category,
        subLabel = {},
        className = '',
        external = false,
        icon
    } = item ?? {}

    const isNavigate = actionId?.startsWith('navigate:')
    const isIntegration = actionId?.startsWith('navigate:integrations:')
    const isIntegrationInstance = actionId?.startsWith('navigate:integrations:instance:')

    const Icon = icon ?? getCategoryIcon(category)
    const iconProps = (isIntegration ? { className: 'integration' } : null)

    const triggerClassName = cls([
        'neutral',
        isActive && 'active',
        !!Icon && 'has-icon',
        className
    ])

    if(isIntegration) {
        if(isIntegrationInstance) {
            subLabel.text = formatMessage({
                id: 'noun_integration_instance',
                defaultMessage: 'Integration connection'
            })
        } else {
            subLabel.text = formatMessage({
                id: 'noun_integration',
                defaultMessage: 'Integration'
            })
        }
    }

    const isClickable = item?.link || item?.onClick
    const Node = isClickable ? Button : Item

    const KeyHint = external ?
        ExternalLink
        : isNavigate ?
            Navigate
            : Enter

    return (
        <ListItem
            className="command"
            style={{ '--item-height': `${itemHeight}px` }}
            ref={itemRef}>
            <Node
                {...(isClickable && { onClick: () => execute(item) })}
                className={triggerClassName}>
                {!!Icon && (
                    <Icon
                        {...iconProps}
                        size={20} />
                )}
                <Label>
                    <span className="label">
                        {getItemLabel(item)}
                    </span>
                    {!!subLabel?.text && (
                        <span className="sub-label">
                            {subLabel.text}
                        </span>
                    )}
                    {(newBadgeItems?.[category]?.actionIds?.includes(actionId) && !dismissedNewlyAdded.includes(`module:${category}`)) && (
                        <NewBadge>
                            <FormattedMessage
                                id="noun_new"
                                defaultMessage="New" />
                        </NewBadge>
                    )}
                </Label>
                {!!isActive && (
                    <AnimatePresence>
                        <KeyHintInner animate={isActive ? 'active' : 'inactive'}>
                            <KeyHint size={16} />
                        </KeyHintInner>
                    </AnimatePresence>
                )}
            </Node>
        </ListItem>
    )
}

export default Command